import { useContext, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { WorkspaceAvatar } from 'src/components/WorkspaceAvatar/WorkspaceAvatar';
import { LOGO_SIZE } from 'src/constants/logoConsts';
import { PortalConfigContext, SidebarThemeContext } from 'src/context';
import {
  typography13MediumStyle,
  typography15MediumStyle,
} from 'src/legacy/components/Text';
import { TruncatedText } from 'src/legacy/components/UI';

export const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  portalName: {
    color: theme.palette.common.white,
    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
  logo: {
    width: '100%',
  },
  logoContainer: {
    width: `${LOGO_SIZE}px`,
    height: `${LOGO_SIZE}px`,
    backgroundColor: 'transparent',
    marginRight: theme.spacing(1),
  },
  previewAvatar: {
    height: '19px',
    width: '19px',
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  noCompanyLogoContainer: {
    width: `${LOGO_SIZE}px`,
    height: `${LOGO_SIZE}px`,
    marginRight: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...typography15MediumStyle,
  },
}));

export interface LogoProps {
  isPreviewLogo?: boolean;
  iconImagePreviewUrl?: string;
  portalNamePreview?: string;
  portalNamePreviewColor?: string;
  portalLogo?: React.ReactNode;
  logoTextColor?: string; // prop to override the portal name text color
}

const Logo: React.FC<LogoProps> = ({
  isPreviewLogo,
  iconImagePreviewUrl,
  portalNamePreview,
  portalLogo = null,
  portalNamePreviewColor,
  logoTextColor = '',
}) => {
  const { classes } = useStyles();
  const portalConfig = useContext(PortalConfigContext);
  const sidebarTheme = useContext(SidebarThemeContext);
  const imageUrl = isPreviewLogo
    ? iconImagePreviewUrl
    : portalConfig.assets.logo.iconImageUrl;
  const portalName = isPreviewLogo ? portalNamePreview : portalConfig.name;

  // for customization page client preview use
  // preview color else use sidebar text color
  const portalNameColor = isPreviewLogo
    ? portalNamePreviewColor
    : sidebarTheme.sidebarTextColor;

  const portalLogoComponent = useMemo(() => {
    if (portalLogo) {
      return portalLogo;
    }
    if (imageUrl) {
      return (
        <WorkspaceAvatar
          containerClassName="mr-2"
          iconSrc={imageUrl}
          workspaceName={portalName || ''}
        />
      );
    }
    return null;
  }, [portalLogo, imageUrl, portalName]);

  return (
    <div className={classes.container}>
      {portalLogoComponent}
      <TruncatedText
        showTitle
        styleProps={{
          ...typography13MediumStyle,
          color: logoTextColor || portalNameColor,
          ...(isPreviewLogo ? { fontSize: '6px', lineHeight: '16px' } : {}),
        }}
        text={portalName || ''}
        maxChars={16.5}
      />
    </div>
  );
};

export default Logo;
